// Modules
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Rxjs
import { Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '@app/general/store/reducers';
import * as AuthSelectors from '@store/selectors/auth.selectors';
import { take } from 'rxjs/operators';

// Services
import { AppConfigService } from '@shared-module/services/app-config/app-config.service';
import { IMSTokenLogin } from '@app/general/shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Private variables
  private _baseUrl: string;
  private _baseUrlAuth: string;

  private _loggedUser$ = this._store$.pipe(
    select(AuthSelectors.selectLoggedUser)
  );

  // Constructor
  constructor(
    private _app: AppConfigService,
    private _httpClient: HttpClient,
    private _store$: Store<AppState>
  ) {}

  // Private method to set the base url auth
  private _setBaseUrlAuth(): void {
    if (!this._baseUrlAuth) this._baseUrlAuth = `${this._app.authBaseUrl}/auth`;
  }

  // Private method to set the base url
  private _setBaseUrl(): void {
    if (!this._baseUrl) this._baseUrl = `${this._app.authBaseUrl}/auth`;
  }

  private _setBaseUrlLogin(): void {
    if (!this._baseUrl) this._baseUrl = `${this._app.authBaseUrl}`;
  }

  // Public method to login
  public login(
    credential: string,
    credentialType: number,
    password: string,
    portal: string
  ): Observable<any> {
    this._setBaseUrl();
    const loginPath = this._baseUrl;
    return this._httpClient.post(loginPath, {
      credential,
      credentialType,
      password,
      portal,
    });
  }

  // Public method to logout
  public logout(): Observable<any> {
    // Clear information
    sessionStorage.clear();
    return of(true);
  }

  // Public method to refresh a session token
  public refreshToken(
    accessToken: string,
    refreshToken: string
  ): Observable<any> {
    this._setBaseUrl();
    const refreshTokenPath = `${this._baseUrl}/refreshtoken`;
    return this._httpClient.post(
      refreshTokenPath,
      { accessToken, refreshToken },
      { observe: 'response' }
    );
  }

  public loginFirstStep(email: string) {
    this._setBaseUrlLogin();
    this._baseUrl = this._baseUrl.substring(0, this._baseUrl.lastIndexOf('/'));
    const loginPath = `${this._baseUrl}/admin/auth/sso`;
    return this._httpClient.post(loginPath, { email: email });
  }

  public loginMSA(email: string) {
    // auth/sso
    this._setBaseUrl();
    const loginPath = `${this._baseUrl}/sso`;
    return this._httpClient.post(loginPath, { email: email });
  }

  public loginWithMSALToken(request: IMSTokenLogin): Observable<any> {
    this._setBaseUrlLogin();
    this._baseUrl = this._baseUrl.substring(0, this._baseUrl.lastIndexOf('/'));
    const loginPath = `${this._baseUrl}/admin/auth/get-token`;
    return this._httpClient.post(
      loginPath,
      { email: request.email },
      {
        headers: {
          Authorization: 'Bearer ' + request.accessToken,
        },
      }
    );
  }

  // Public method to init password recovery
  public requestPasswordRecovery(email: string): Observable<any> {
    this._setBaseUrl();
    const requestPasswordRecoveryPath = `${this._baseUrl}/initrecovery`;
    return this._httpClient.post(requestPasswordRecoveryPath, { email });
  }

  // Public method to check password recovery validity
  public checkPasswordRecoveryValidity(key: string): Observable<any> {
    this._setBaseUrl();
    const passwordRecoveryValidityPath = `${this._baseUrl}/checkrecovery?key=${key}`;
    return this._httpClient.get(passwordRecoveryValidityPath);
  }

  // Public method to check user activation validity
  public checkActivationValidity(key: string): Observable<any> {
    this._setBaseUrl();
    const activationValidityPath = `${this._baseUrl}/checkactivation?key=${key}`;
    return this._httpClient.get(activationValidityPath);
  }

  // Public method to change the password from recovery
  public passwordRecoveryChange(
    key: string,
    newPassword: string
  ): Observable<any> {
    this._setBaseUrl();
    const passwordRecoveryChangePath = `${this._baseUrl}/recovery`;
    return this._httpClient.post(passwordRecoveryChangePath, {
      key,
      newPassword,
    });
  }

  // Public method to activate user from activation
  public activateUser(key: string, password: string): Observable<any> {
    this._setBaseUrl();
    const activationPath = `${this._baseUrl}/activate`;
    return this._httpClient.post(activationPath, {
      key,
      password,
    });
  }

  // Public method to change the password
  public changePassword(
    email: string,
    newPassword: string,
    currentPassword: string
  ): Observable<any> {
    const changePasswordPath = `${this._app.authBaseUrl}/auth/changepassword`;
    return this._httpClient.post(changePasswordPath, {
      email,
      newPassword,
      currentPassword,
    });
  }

  // Public method to get config column table
  public getConfigListados(key: string): Observable<any> {
    let accessToken;
    this._loggedUser$.pipe(take(1)).subscribe(user => {
      accessToken = user.accessToken;
    });
    this._setBaseUrlAuth();
    const configListadosPath = `${this._baseUrlAuth}/configListados?key=${key}`;
    return this._httpClient.get(configListadosPath, {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    });
  }

  // Public method to set config column table
  public setConfigListados(key: string, config: any): Observable<any> {
    let accessToken;
    this._loggedUser$.pipe(take(1)).subscribe(user => {
      accessToken = user.accessToken;
    });
    this._setBaseUrlAuth();
    const configListadosPath = `${this._baseUrlAuth}/configListados`;
    return this._httpClient.post(
      configListadosPath,
      {
        key,
        config: JSON.stringify(config),
      },
      {
        headers: {
          Authorization: 'Bearer ' + accessToken,
        },
      }
    );
  }
}
